import React, { FC, useMemo, useRef, useState } from 'react';

import { Button } from 'devextreme-react/button';
import DataGrid, {
  Column,
  Export,
  FilterPanel,
  FilterRow,
  HeaderFilter,
  Pager,
  Paging,
  StateStoring,
} from 'devextreme-react/data-grid';
import 'devextreme-react/text-area';

import { AppCard, AppLayoutTop } from 'components/appComponents';
import ClaimsProvider from 'components/claimsBlock';
import { MONEY_FORMAT } from 'constants/formats';
import { CheckBox } from 'devextreme-react';
import $createStore from 'utils/create-store-instance';
import { onExporting } from 'utils/exportingExcel';

import { useChangeCashFlowExpenseSalaryDetailIssuedMutation } from 'bus/cash';

import { AutoFill, CreateEditModal, DeleteModal } from './components';

type Props = {
  cashFlowId: number | string;
  refreshForm: () => void;
};

const CashFlowExpenseSalaryDetail: FC<Props> = ({ cashFlowId, refreshForm }) => {
  const refGrid = useRef<DataGrid | null>(null);

  const [modalIsVisible, setModalIsVisible] = useState(false);
  const [modalIsVisibleAutoFill, setModalIsVisibleAutoFill] = useState(false);
  const [modalIsVisibleDelete, setModalIsVisibleDelete] = useState(false);
  const [detailId, setDetailId] = useState<number | null>(null);

  const dataSource: any = useMemo(() => {
    return $createStore(`/v1/cashFlowExpenseSalary/${cashFlowId}/details`, 'id');
  }, [cashFlowId]);

  const refresh = () => {
    refGrid.current?.instance.refresh();
    refreshForm();
  };

  const [cashFlowExpenseSalaryDetailIssued] = useChangeCashFlowExpenseSalaryDetailIssuedMutation();

  const changeIssued = async (id: number) => {
    await cashFlowExpenseSalaryDetailIssued({
      cashFlowId,
      detailId: id,
    });
    refresh();
  };

  return (
    <div className="container-dev">
      <div className="pt-1">
        <ClaimsProvider claimsBlock={['CanCreateSalaryDetails']}>
          <AppLayoutTop title="">
            <Button
              width={120}
              text="Добавить"
              type="default"
              onClick={() => {
                setDetailId(null);
                setModalIsVisible(true);
              }}
            />
            <Button
              width={170}
              text="Автозаполнение"
              type="default"
              className="ml-1"
              onClick={() => {
                setModalIsVisibleAutoFill(true);
              }}
            />
          </AppLayoutTop>
        </ClaimsProvider>
      </div>
      {modalIsVisible && (
        <CreateEditModal
          changeVisible={setModalIsVisible}
          id={detailId}
          refresh={refresh}
          visible={modalIsVisible}
          cashFlowId={+cashFlowId}
        />
      )}

      {modalIsVisibleDelete && (
        <DeleteModal
          changeVisible={setModalIsVisibleDelete}
          visible={modalIsVisibleDelete}
          refresh={refresh}
          detailId={detailId}
          cashFlowId={+cashFlowId}
        />
      )}
      {modalIsVisibleAutoFill && (
        <AutoFill
          cashFlowId={+cashFlowId}
          changeVisible={setModalIsVisibleAutoFill}
          visible={modalIsVisibleAutoFill}
          refresh={refresh}
        />
      )}

      <AppCard>
        <DataGrid
          dataSource={dataSource}
          showBorders={false}
          loadPanel={{
            enabled: true,
            text: 'Загрузка',
          }}
          ref={refGrid}
          remoteOperations={true}
          onExporting={onExporting}
          allowColumnResizing
          columnResizingMode="nextColumn"
          columnMinWidth={50}
          allowColumnReordering
        >
          <StateStoring enabled={true} type="localStorage" storageKey="CashFlowExpenseSalaryDetail" />
          <Export enabled={true} />
          <FilterRow visible={true} />
          <FilterPanel visible={true} />
          <HeaderFilter visible />
          <Column dataField="employeeName" caption="ФИО" />
          <Column dataField="post" caption="Должность" />
          <Column dataField="toPay" caption="Сумма руб." format={MONEY_FORMAT} />
          <Column
            dataField="issued"
            caption="Выдано"
            cellRender={(data) => (
              <CheckBox value={data.data.issued} onValueChanged={() => changeIssued(data.data.id)} />
            )}
          />
          <Column dataField="requisites" caption="Реквизиты" />
          {/* <Column dataField="dateBegin" caption="Дата начала" dataType="date" />
<Column dataField="dateEnd" caption="Дата окончания" dataType="date" /> */}
          <Column dataField="comment" caption="Комментарий" />
          <Column
            dataField="actions"
            caption=""
            width={120}
            allowFiltering={false}
            allowSorting={false}
            cellRender={(data) => (
              <>
                <ClaimsProvider claimsBlock={['CanEditSalaryDetails']}>
                  <Button
                    icon="edit"
                    type="default"
                    stylingMode="text"
                    onClick={() => {
                      setDetailId(data.data.id);
                      setModalIsVisible(true);
                    }}
                  />
                </ClaimsProvider>
                <ClaimsProvider claimsBlock={['CanDeleteSalaryDetails']}>
                  <Button
                    icon="trash"
                    type="default"
                    stylingMode="text"
                    onClick={() => {
                      setDetailId(data.data.id);
                      setModalIsVisibleDelete(true);
                    }}
                  />
                </ClaimsProvider>
              </>
            )}
          />

          <Paging defaultPageSize={15} />
          <Pager showPageSizeSelector={true} allowedPageSizes={[15, 30, 50, 100]} />
        </DataGrid>
      </AppCard>
    </div>
  );
};

export default CashFlowExpenseSalaryDetail;
